import { useState } from 'react'
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Tooltip as RechartsTooltip,
	ReferenceLine,
	ResponsiveContainer,
	XAxis,
	YAxis,
} from 'recharts'
import { Text } from '../Text'
import { getRatingColor, VsOverall } from './shared'
import { useChartCursor } from './useChartCursor'

export interface BenchmarkDeal {
	dealName: string
	rating: number
}

const BAR_COLOR = '#BAD6FD'
const COMPANY_OVERALL_COLOR = '#3B82F6'
const TOP_10_COLOR = '#10B981'

type ChartDatum = { dealName: string; rating: number; overallRating: number | null; top10Rating: number | null }

type RechartsTooltipPayloadEntry = { payload: ChartDatum }

function BenchmarkTooltip({
	active,
	payload,
	scaleMax,
}: Readonly<{
	active?: boolean
	payload?: RechartsTooltipPayloadEntry[]
	scaleMax: number
}>) {
	if (!active || !payload?.length) return null
	const datum = payload[0].payload
	const vsCompany = datum.overallRating === null ? null : Number((datum.rating - datum.overallRating).toFixed(2))
	const vsTop10 = datum.top10Rating === null ? null : Number((datum.rating - datum.top10Rating).toFixed(2))

	return (
		<div className='rounded-lg border border-neutral-200 bg-white px-4 py-3 shadow-md text-sm flex flex-col gap-3 min-w-44'>
			<div className='flex flex-col gap-1.5'>
				<span className='text-sm font-medium text-neutral-900'>Deal Rating</span>
				<span
					className={`inline-flex items-center justify-center rounded-full px-3 py-1 text-sm font-semibold w-fit ${getRatingColor(datum.rating, scaleMax)}`}>
					{datum.rating.toFixed(2)}
				</span>
			</div>
			<div className='flex flex-col gap-1'>
				<span className='text-sm font-medium text-neutral-900'>vs. Company Overall</span>
				<VsOverall value={vsCompany} />
			</div>
			<div className='flex flex-col gap-1'>
				<span className='text-sm font-medium text-neutral-900'>vs. Top 10% Deals</span>
				<VsOverall value={vsTop10} />
			</div>
		</div>
	)
}

export function BenchmarkVsCompanyChart({
	deals,
	overallRating,
	top10PercentRating,
	scaleMax = 10,
}: Readonly<{
	deals: BenchmarkDeal[]
	overallRating: number | null
	top10PercentRating: number | null
	scaleMax?: number
}>) {
	const { containerRef, cursorPos, onMouseMove, onMouseLeave: onContainerMouseLeave } = useChartCursor(185)
	const [activeIndex, setActiveIndex] = useState<number | null>(null)

	if (deals.length === 0) {
		return (
			<div className='rounded-lg border border-neutral-200 bg-white p-6 flex flex-col gap-4 text-left'>
				<Text variant='medium' weight='semibold'>
					Benchmark vs. Company
				</Text>
				<div className='flex h-48 items-center justify-center text-neutral-400 text-sm'>
					No deals with ratings to benchmark.
				</div>
			</div>
		)
	}

	const chartData: ChartDatum[] = deals.map((d) => ({
		dealName: d.dealName,
		rating: d.rating,
		overallRating,
		top10Rating: top10PercentRating,
	}))

	return (
		<div className='rounded-lg border border-neutral-200 bg-white p-6 flex flex-col gap-4 text-left'>
			<Text variant='medium' weight='semibold'>
				Benchmark vs. Company
			</Text>
			<div
				ref={containerRef}
				role='img'
				aria-label='Benchmark vs. Company bar chart'
				style={{ height: 280 }}
				onMouseMove={onMouseMove}
				onMouseLeave={onContainerMouseLeave}>
				<ResponsiveContainer width='100%' height='100%'>
					<BarChart
						data={chartData}
						margin={{ top: 10, right: 24, left: 0, bottom: 30 }}
						onMouseLeave={() => setActiveIndex(null)}>
						<CartesianGrid strokeDasharray='3 3' stroke='#F3F4F6' vertical={false} />
						<XAxis
							dataKey='dealName'
							tick={{ fontSize: 11, fill: '#6B7280' }}
							axisLine={false}
							tickLine={false}
							interval={0}
							angle={0}
							textAnchor='middle'
							height={36}
							tickFormatter={(value: string) => {
								const stripped = value.replace(/^Project\s+/i, '')
								return stripped.length > 10 ? `${stripped.slice(0, 10)}…` : stripped
							}}
						/>
						<YAxis
							domain={[0, scaleMax]}
							ticks={Array.from({ length: 6 }, (_, i) => (i * scaleMax) / 5)}
							tick={{ fontSize: 12, fill: '#9CA3AF' }}
							axisLine={false}
							tickLine={false}
						/>
						<RechartsTooltip
							cursor={{ fill: '#F8FAFC' }}
							content={<BenchmarkTooltip scaleMax={scaleMax} />}
							position={cursorPos}
							isAnimationActive={false}
						/>
						<Bar
							dataKey='rating'
							radius={[4, 4, 0, 0]}
							onMouseEnter={(_, index) => setActiveIndex(index)}
							onMouseLeave={() => setActiveIndex(null)}>
							{chartData.map((d, i) => (
								<Cell key={d.dealName} fill={BAR_COLOR} opacity={activeIndex !== null && activeIndex !== i ? 0.3 : 1} />
							))}
						</Bar>
						{overallRating !== null && (
							<ReferenceLine
								y={overallRating}
								stroke={COMPANY_OVERALL_COLOR}
								strokeWidth={2}
								ifOverflow='extendDomain'
							/>
						)}
						{top10PercentRating !== null && (
							<ReferenceLine y={top10PercentRating} stroke={TOP_10_COLOR} strokeWidth={2} ifOverflow='extendDomain' />
						)}
					</BarChart>
				</ResponsiveContainer>
			</div>
			<div className='mt-auto flex items-center justify-center gap-6 text-xs text-neutral-600'>
				<div className='flex items-center gap-2'>
					<span className='inline-block h-0.5 w-4' style={{ backgroundColor: COMPANY_OVERALL_COLOR }} />
					<span>Company Overall</span>
				</div>
				<div className='flex items-center gap-2'>
					<span className='inline-block h-0.5 w-4' style={{ backgroundColor: TOP_10_COLOR }} />
					<span>Top 10% Deals</span>
				</div>
			</div>
		</div>
	)
}
